/**
 * Runtime configuration, fetched before React mounts.
 *
 * These values are NOT secret — a user pool id, an app client id and an API
 * URL are all public identifiers, and the SPA's privileges come from the token
 * the user signs in for, never from the bundle. They are loaded at runtime
 * rather than baked in at build time so one artefact can be deployed to both
 * environments; baking them in is how a dev bundle reaches prod.
 */
export interface AppConfig {
  /** Cognito issuer, e.g. https://cognito-idp.eu-west-1.amazonaws.com/{poolId} */
  authority: string;
  clientId: string;
  /** Cognito hosted-UI domain, used for sign-out. */
  hostedUiDomain: string;
  /** API Gateway base URL. Product routes hang off it: {apiUrl}/mcp/admin/... */
  apiUrl: string;
  env: string;
  /**
   * Whether the pool actually has a Google identity provider.
   *
   * The SPA cannot assume it: asking Cognito for `identity_provider=Google`
   * when none is configured is refused with "Login option is not available".
   * Absent means false, so a config.json written before this existed keeps
   * working and shows the password form.
   */
  googleEnabled?: boolean;
}

let cached: AppConfig | null = null;

export async function loadConfig(): Promise<AppConfig> {
  if (cached) return cached;

  const resp = await fetch("/config.json", { cache: "no-store" });
  if (!resp.ok) {
    throw new Error(
      `Could not load /config.json (${resp.status}). It is written by the ` +
        `deploy from CDK outputs; in local dev, copy web/config.example.json ` +
        `to web/public/config.json and fill in the deployed dev values.`,
    );
  }

  const body = await resp.text();

  // A missing key on a private bucket returns 403, and CloudFront maps both 403
  // and 404 to /index.html so client-side routing works — so a MISSING
  // config.json arrives here as the HTML page, with a 200. JSON.parse then fails
  // with "Unexpected token '<'", which is accurate and tells you nothing.
  //
  // Catch it explicitly and name the actual cause: whoever deployed skipped the
  // step that uploads this file.
  if (body.trimStart().startsWith("<")) {
    throw new Error(
      "/config.json is missing — the server returned the SPA's index.html " +
        "instead (CloudFront falls back to it for any key that does not exist).\n\n" +
        "The deploy step that writes config.json from CDK outputs did not run. " +
        "To fix it by hand:\n" +
        "  python3 scripts/write_spa_config.py \\\n" +
        "    infrastructure/cdk-outputs.json <stack-name> <env> > /tmp/config.json\n" +
        "  aws s3 cp /tmp/config.json s3://<spa-bucket>/config.json \\\n" +
        "    --cache-control 'no-store, max-age=0' --content-type application/json\n" +
        "  aws cloudfront create-invalidation --distribution-id <id> --paths /config.json",
    );
  }

  let cfg: Partial<AppConfig>;
  try {
    cfg = JSON.parse(body) as Partial<AppConfig>;
  } catch {
    throw new Error(`/config.json is not valid JSON. First bytes: ${body.slice(0, 80)}`);
  }

  for (const key of ["authority", "clientId", "apiUrl"] as const) {
    if (!cfg[key]) {
      throw new Error(`config.json is missing "${key}".`);
    }
  }

  cached = cfg as AppConfig;
  return cached;
}

/** Test seam. */
export function __resetConfig(): void {
  cached = null;
}
